import React, { useEffect, useState, useRef } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Grid,
  Box,
  styled,
  CircularProgress,
  Skeleton, // Import Skeleton for loading indicators
} from "@mui/material";
import {
  Menu as MenuIcon,
  Search,
  ArrowBackIos,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import HeadphonesIcon from '@mui/icons-material/Headphones';
import pravachanIcon from '../../../src/svg/pravachanHome.svg';
import magazinesIcon from '../../../src/svg/magazineHome.svg';
import audioIcon from '../../../src/svg/audioHome.svg';
import videoIcon from '../../../src/svg/ic_video_home.svg';
import articlesIcon from '../../../src/svg/articlesHome.svg';
import ArticleIcon from '@mui/icons-material/Article';
import VideocamIcon from '@mui/icons-material/Videocam';
import PersonIcon from '@mui/icons-material/Person';
import MuiAccordionSummary, {
  AccordionSummaryProps,
  accordionSummaryClasses,
} from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import { MobileHome } from "./MobileHome";
import { useNavigate } from "react-router-dom";
import { Product } from './MobileMagazines';
import ArticlesService from "../../Services/Articles";
import VideoService from "../../Services/VideoPlayer";
import { toSeoSlug } from "../../utils/seoSlug";

// --- Styled Accordion Components ---

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&::before': {
    display: 'none',
  },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<KeyboardArrowDownIcon sx={{ fontSize: '0.9rem' }} />} // Use the down arrow icon
    {...props}
  />
))(({ theme }) => ({
  backgroundColor: '#FFEBD5',
  flexDirection: 'row', // Content then icon
  [`& .${accordionSummaryClasses.expandIconWrapper}`]: {
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
    transform: 'rotate(0deg)', // Down arrow points down by default
  },
  [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]:
  {
    transform: 'rotate(180deg)', // Rotate 180 degrees to point up when expanded
  },
  [`& .${accordionSummaryClasses.content}`]: {
    marginLeft: 0,
    flexGrow: 1,
    justifyContent: 'flex-start',
    paddingRight: theme.spacing(1), // Space between text and arrow
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: '1px solid rgba(0, 0, 0, .125)',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  msOverflowStyle: 'none',
  scrollbarWidth: 'none'
}));

// --- Categories Accordion Component ---

export interface CategoryItem {
  id: number;
  name: string;
}

export interface KalyansItem {
  id: any;
  name: string;
}
export interface KalpataruItem {
  id: number;
  name: string;
}
export interface VivekVaniItem {
  id: number;
  name: string
}

export interface AudioItem {
  id: number;
  name: string;
}
export interface PravachanItem {
  id: number | string;
  name: string;
}
export interface VideoItem {
  id: number;
  name: string;
  slug?: string;
}
export interface ArticleItem {
  id: number;
  name: string;
  slug?: string;
}
export interface SubjectItem {
  id: number;
  name: string;
}
export interface audiosItem {
  id: number;
  name: string;
}
export interface videosItem {
  id: number;
  name: string;
  slug?: string;
}
export interface articlesItem {
  id: number;
  name: string;
  slug?: string;
}
export interface CategoriesAccordionProps {
  categories: CategoryItem[];
  kalyans: KalyansItem[];
  loadingCategories: boolean; // Prop to indicate loading state
  kalpataru: KalpataruItem[];
  vivekVani: VivekVaniItem[];
  audios: audiosItem[];
  pravachans: PravachanItem[];
  videos: videosItem[];
  articles: articlesItem[];
}

export const CategoriesAccordion: React.FC<CategoriesAccordionProps> = ({ categories, kalyans, loadingCategories, kalpataru, vivekVani, audios, pravachans, videos, articles }) => {
  const [expanded, setExpanded] = useState<string | false>(false);
  const { t } = useTranslation();

  const handleChange =
    (panelId: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
      setExpanded(newExpanded ? panelId : false);
    };

  const renderSkeleton = (count: number) => (
    <>
      {Array.from(new Array(count)).map((_, index) => (
        <Box key={index} sx={{ borderBottom: "1px solid whitesmoke", paddingTop: 1.2, paddingBottom: 1.2, marginRight: -2, marginLeft: -2, paddingLeft: 2 }}>
          <Skeleton variant="text" width="80%" height={20} />
        </Box>
      ))}
    </>
  );
  const navigate = useNavigate();

  // Public URLs use slug only — resolve from the category detail API when the list omits it.
  const resolveCategorySlug = async (
    item: { id?: string | number; slug?: string },
    fetchSlug: (id: string) => Promise<any>,
    logPrefix: string
  ): Promise<string> => {
    const logdetails: string[] = [logPrefix, `id=${item.id || "none"}`, `slug=${item.slug || "none"}`];
    let slug = toSeoSlug(item);
    if (!slug && item.id) {
      try {
        const res = await fetchSlug(String(item.id));
        slug = toSeoSlug({ slug: res?.result?.slug, id: res?.result?.id });
        logdetails.push(`resolved_slug=${slug || "none"}`);
      } catch (error: any) {
        logdetails.push(`resolve_error=${error?.message || "unknown"}`);
      }
    }
    if (!slug) {
      logdetails.push("abort=missing_slug");
      console.log("[CategoriesAccordion]", logdetails.join(" | "));
      return "";
    }
    logdetails.push(`seo_slug=${slug}`);
    console.log("[CategoriesAccordion]", logdetails.join(" | "));
    return slug;
  };

  const handleItemClick = (id: number | string) => {
    console.log("Clicked item id:", id);
    // navigate('/mbBooks', { state: { selectedCategoryId: id , kalyanId:id , magazineId:id } })
    setId(id.toString());
  };
  const [id, setId] = useState<string | null>(null);
  return (
    <Box sx={{ p: 2 }} >
      {loadingCategories ? (
        // Loading Skeleton
        <>
          <Accordion
            style={{ backgroundColor: "#FFEBD5", margin: -14, marginTop: -35, border: "2px solid white" }}
            expanded={false} // Keep closed during loading
          >
            <AccordionSummary>
              <Skeleton variant="text" width="60%" height={28} />
            </AccordionSummary>
            <AccordionDetails style={{ backgroundColor: "white" }}>
              {renderSkeleton(3)} {/* Render 3 skeleton lines for categories */}
            </AccordionDetails>
          </Accordion>

          <Accordion
            expanded={false}
            sx={{
              backgroundColor: "#FFEBD5",
              margin: "-14px",
              marginTop: "10px",
              border: "2px solid white",
              '&.Mui-expanded': {
                margin: "-14px",
                marginTop: "10px",
                minHeight: 'auto',
              },
              '& .MuiAccordionSummary-root': {
                minHeight: '48px',
              }
            }}
          >
            <AccordionSummary expandIcon={null} sx={{ cursor: 'default' }}>
              <Skeleton variant="text" width="70%" height={28} />
            </AccordionSummary>
          </Accordion>

          <Accordion
            expanded={false}
            sx={{
              backgroundColor: "#FFEBD5",
              margin: "-14px",
              marginTop: "10px",
              border: "2px solid white",
              '&.Mui-expanded': {
                margin: "-14px",
                marginTop: "10px",
                minHeight: 'auto',
              },
              '& .MuiAccordionSummary-root': {
                minHeight: '48px',
              }
            }}
          >
            <AccordionSummary expandIcon={null} sx={{ cursor: 'default' }}>
              <Skeleton variant="text" width="70%" height={28} />
            </AccordionSummary>
          </Accordion>

          <Accordion
            sx={{
              backgroundColor: "#FFEBD5",
              margin: "-14px",
              marginTop: "10px",
              border: "2px solid white",
              '&.Mui-expanded': {
                margin: "-14px",
                marginTop: "10px",
                minHeight: 'auto',
              },
              '& .MuiAccordionSummary-root': {
                minHeight: '48px',
              }
            }}
            expanded={false}
          >
            <AccordionSummary>
              <Skeleton variant="text" width="60%" height={28} />
            </AccordionSummary>
            <AccordionDetails style={{ backgroundColor: "white" }}>
              {renderSkeleton(3)} {/* Render 3 skeleton lines for Kalyans */}
            </AccordionDetails>
          </Accordion>

          <Accordion
            sx={{
              backgroundColor: "#FFEBD5",
              margin: "-14px",
              marginTop: "10px",
              border: "2px solid white",
              '&.Mui-expanded': {
                margin: "-14px",
                marginTop: "10px",
                minHeight: 'auto',
              },
              '& .MuiAccordionSummary-root': {
                minHeight: '48px',
              }
            }}
            expanded={false}
          >
            <AccordionSummary>
              <Skeleton variant="text" width="60%" height={28} />
            </AccordionSummary>
            <AccordionDetails style={{ backgroundColor: "white" }}>
              {renderSkeleton(2)} {/* Render 2 skeleton lines for Kalpataru */}
            </AccordionDetails>
          </Accordion>

          <Accordion
            sx={{
              backgroundColor: "#FFEBD5",
              margin: "-14px",
              marginTop: "10px",
              border: "2px solid white",
              '&.Mui-expanded': {
                margin: "-14px",
                marginTop: "10px",
                minHeight: 'auto',
              },
              '& .MuiAccordionSummary-root': {
                minHeight: '48px',
              }
            }}
            expanded={false}
          >
            <AccordionSummary>
              <Skeleton variant="text" width="60%" height={28} />
            </AccordionSummary>
            <AccordionDetails style={{ backgroundColor: "white" }}>
              {renderSkeleton(2)} {/* Render 2 skeleton lines for VivekVani */}
            </AccordionDetails>
          </Accordion>

          <Accordion
            sx={{
              backgroundColor: "#FFEBD5",
              margin: "-14px",
              marginTop: "10px",
              border: "2px solid white",
              '&.Mui-expanded': {
                margin: "-14px",
                marginTop: "10px",
                minHeight: 'auto',
              },
              '& .MuiAccordionSummary-root': {
                minHeight: '48px',
              }
            }}
            expanded={false}
          >
            <AccordionSummary>
              <Skeleton variant="text" width="60%" height={28} />
            </AccordionSummary>
            <AccordionDetails style={{ backgroundColor: "white" }}>
              {renderSkeleton(3)} {/* Render 3 skeleton lines for Audios */}
            </AccordionDetails>
          </Accordion>

          <Accordion
            sx={{
              backgroundColor: "#FFEBD5",
              margin: "-14px",
              marginTop: "10px",
              border: "2px solid white",
              '&.Mui-expanded': {
                margin: "-14px",
                marginTop: "10px",
                minHeight: 'auto',
              },
              '& .MuiAccordionSummary-root': {
                minHeight: '48px',
              }
            }}
            expanded={false}
          >
            <AccordionSummary>
              <Skeleton variant="text" width="60%" height={28} />
            </AccordionSummary>
            <AccordionDetails style={{ backgroundColor: "white" }}>
              {renderSkeleton(3)} {/* Render 3 skeleton lines for Pravachan */}
            </AccordionDetails>
          </Accordion>

          <Accordion
            sx={{
              backgroundColor: "#FFEBD5",
              margin: "-14px",
              marginTop: "10px",
              border: "2px solid white",
              '&.Mui-expanded': {
                margin: "-14px",
                marginTop: "10px",
                minHeight: 'auto',
              },
              '& .MuiAccordionSummary-root': {
                minHeight: '48px',
              }
            }}
            expanded={false}
          >
            <AccordionSummary>
              <Skeleton variant="text" width="60%" height={28} />
            </AccordionSummary>
            <AccordionDetails style={{ backgroundColor: "white" }}>
              {renderSkeleton(3)} {/* Render 3 skeleton lines for Videos */}
            </AccordionDetails>
          </Accordion>
        </>
      ) : (
        <>
          {categories.length === 0 && kalyans.length === 0 && kalpataru.length === 0 && vivekVani.length === 0 && audios.length === 0 && pravachans.length === 0 && videos.length === 0 ? (
            <Typography sx={{ textAlign: 'center', mt: 2, color: 'text.secondary' }}>
              No categories or content found.
            </Typography>
          ) : (
            <>
              {/* E_books_tr Accordion */}
              <Accordion
                style={{ backgroundColor: "#FFEBD5", margin: -14, marginTop: -35, border: "2px solid white" }}
                expanded={expanded === 'eBooksPanel'}
                onChange={handleChange('eBooksPanel')}
              >
                <AccordionSummary>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MenuBookIcon sx={{ fontSize: 18, color: "#f97316" }} />
                    <Typography component="span" sx={{ fontFamily: 'chanakyaUni', fontSize: 18, fontWeight: 600, color: "#5f452c" }}>{t("E_books_tr")}</Typography>
                  </Box>
                </AccordionSummary>

                <AccordionDetails style={{ backgroundColor: "white" }} >
                  {categories.length > 0 ? (

                    categories.map((category: CategoryItem) => (

                      <Typography onClick={()=>navigate("/books",{state:{selectedCategoryId:category.id, categoryName:category.name}})} key={category.id} sx={{ color: 'text.secondary', fontSize: 12, borderBottom: "1px solid whitesmoke", paddingTop: 1.2, paddingBottom: 1.2, marginRight: -2, marginLeft: -2, paddingLeft: 2 }}>
                        <MenuBookIcon sx={{ fontSize: 14, color: "#f97316", verticalAlign: 'middle', mr: 1 }} />
                        {category.name}
                      </Typography>
                    ))
                  ) : (
                    <Typography sx={{ color: 'text.secondary', fontSize: 12, paddingLeft: 2 }}>
                      No E-book categories found.
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>

              {/* New Non-Expandable Accordion - Monthly Magazine Hindi */}
              <Accordion
                expanded={false} // This makes it non-expandable
                sx={{
                  backgroundColor: "#FFEBD5",
                  margin: "-14px",
                  marginTop: "10px",
                  border: "2px solid white",
                  '&.Mui-expanded': {
                    margin: "-14px",
                    marginTop: "10px",
                    minHeight: 'auto',
                  },
                  '& .MuiAccordionSummary-root': {
                    minHeight: '48px',
                  }
                }}
              >
                <AccordionSummary
                  onClick={() => {
                    const logdetails: string[] = [];
                    logdetails.push("action=geetgovind_hindi_menu_click");
                    logdetails.push("path=/magazines/geetgovind/lang/hindi");
                    navigate('/magazines/geetgovind/lang/hindi', {
                      state: {
                        type: 'geetGovind',
                        showGeetGovindList: true,
                        geetGovindLang: 'hindi',
                        magazineLanguage: 'hindi',
                        magazineLang: 'hindi',
                      },
                    });
                    console.log('[CategoriesAccordion]', logdetails.join(' | '));
                  }}
                  aria-controls="panel-static-hindi-content"
                  id="panel-static-hindi-header"
                  expandIcon={null} // Remove expand icon
                  sx={{ cursor: 'default' }} // Remove pointer cursor
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <img src={magazinesIcon} alt="Magazine" style={{ width: 18, height: 18 }} />
                    <Typography component="span" sx={{ fontFamily: 'chanakyaUni', fontSize: 18, fontWeight: 600, color: "#5f452c" }}>
                      {t("MonthlyMagazine_lang_hindi_tr")}
                    </Typography>
                  </Box>
                </AccordionSummary>

                <AccordionDetails style={{ backgroundColor: "white" }}>
                  <Typography sx={{ color: 'text.secondary', fontSize: 12, paddingLeft: 2 }}>
                    Content for Monthly Magazine Hindi.
                  </Typography>
                </AccordionDetails>
              </Accordion>

              {/* New Non-Expandable Accordion - Monthly Magazine English */}
              <Accordion
                expanded={false} // This makes it non-expandable
                sx={{
                  backgroundColor: "#FFEBD5",
                  margin: "-14px",
                  marginTop: "10px",
                  border: "2px solid white",
                  '&.Mui-expanded': {
                    margin: "-14px",
                    marginTop: "10px",
                    minHeight: 'auto',
                  },
                  '& .MuiAccordionSummary-root': {
                    minHeight: '48px',
                  }
                }}
              >
                <AccordionSummary
                   onClick={() => {
                     const logdetails: string[] = [];
                     logdetails.push("action=geetgovind_english_menu_click");
                     logdetails.push("path=/magazines/geetgovind/lang/english");
                     navigate('/magazines/geetgovind/lang/english', {
                       state: {
                         type: 'geetGovind',
                         showGeetGovindList: true,
                         geetGovindLang: 'english',
                         magazineLanguage: 'english',
                         magazineLang: 'english',
                       },
                     });
                     console.log('[CategoriesAccordion]', logdetails.join(' | '));
                   }}
                  aria-controls="panel-static-english-content"
                  id="panel-static-english-header"
                  expandIcon={null} // Remove expand icon
                  sx={{ cursor: 'default' }} // Remove pointer cursor
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <img src={magazinesIcon} alt="Magazine" style={{ width: 18, height: 18 }} />
                    <Typography component="span" sx={{ fontFamily: 'chanakyaUni', fontSize: 18, fontWeight: 600, color: "#5f452c" }}>
                      {t("MonthlyMagazine_lang_english_tr")}
                    </Typography>
                  </Box>
                </AccordionSummary>

                <AccordionDetails style={{ backgroundColor: "white" }}>
                  <Typography sx={{ color: 'text.secondary', fontSize: 12, paddingLeft: 2 }}>
                    Content for Monthly Magazine English.
                  </Typography>
                </AccordionDetails>
              </Accordion>

              {/* Kalyan Accordion */}
              <Accordion
                sx={{
                  backgroundColor: "#FFEBD5",
                  margin: "-14px",
                  marginTop: "10px",
                  border: "2px solid white",
                  '&.Mui-expanded': {
                    margin: "-14px",
                    marginTop: "10px",
                    minHeight: 'auto',
                  },
                  '& .MuiAccordionSummary-root': {
                    minHeight: '48px',
                  }
                }}
                expanded={expanded === 'kalyanPanel'}
                onChange={handleChange('kalyanPanel')}
              >
                <AccordionSummary>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <img src={magazinesIcon} alt="Magazine" style={{ width: 18, height: 18 }} />
                    <Typography component="span" sx={{ fontFamily: 'chanakyaUni', fontSize: 18, fontWeight: 600, color: "#5f452c" }}>{t("Kalyan_tr")}</Typography>
                  </Box>
                </AccordionSummary>

                <AccordionDetails style={{ backgroundColor: "white" }}>
                  {kalyans.length > 0 ? (
                    kalyans.map((kalyan: KalyansItem) => (
                      <Typography onClick={()=>navigate("/magazines/kalyan/category/" + kalyan.id,{state:{kalyansId:kalyan.id, kalyanName:kalyan.name}})} key={kalyan.id} sx={{ color: 'text.secondary', fontSize: 12, borderBottom: "1px solid whitesmoke", paddingTop: 1.2, paddingBottom: 1.2, marginRight: -2, marginLeft: -2, paddingLeft: 2 }}>
                        <img src={magazinesIcon} alt="" style={{ width: 14, height: 14, verticalAlign: 'middle', marginRight: 8 }} />
                        {kalyan.name}
                      </Typography>
                    ))
                  ) : (
                    <Typography sx={{ color: 'text.secondary', fontSize: 12, paddingLeft: 2 }}>
                      No Kalyans found.
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>

              {/* Kalyan Kalpataru Accordion */}
              <Accordion
                sx={{
                  backgroundColor: "#FFEBD5",
                  margin: "-14px",
                  marginTop: "10px",
                  border: "2px solid white",
                  '&.Mui-expanded': {
                    margin: "-14px",
                    marginTop: "10px",
                    minHeight: 'auto',
                  },
                  '& .MuiAccordionSummary-root': {
                    minHeight: '48px',
                  }
                }}
                expanded={expanded === 'kalpataruPanel'}
                onChange={handleChange('kalpataruPanel')}
              >
                <AccordionSummary>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <img src={magazinesIcon} alt="Magazine" style={{ width: 18, height: 18 }} />
                    <Typography component="span" sx={{ fontFamily: 'chanakyaUni', fontSize: 18, fontWeight: 600, color: "#5f452c" }}>{t("Kalyan_Kalpataru_tr")}</Typography>
                  </Box>
                </AccordionSummary>

                <AccordionDetails style={{ backgroundColor: "white" }}>
                  {kalpataru.length > 0 ? (
                    kalpataru.map((item: KalpataruItem) => (
                      <Typography onClick={()=>navigate("/magazines/kalyanakalpataru/category/" + item.id,{state:{kalpataruId:item.id, kalpataruName:item.name, isKalpataruBook: true}})} key={item.id} sx={{ color: 'text.secondary', fontSize: 12, borderBottom: "1px solid whitesmoke", paddingTop: 1.2, paddingBottom: 1.2, marginRight: -2, marginLeft: -2, paddingLeft: 2 }}>
                        <img src={magazinesIcon} alt="" style={{ width: 14, height: 14, verticalAlign: 'middle', marginRight: 8 }} />
                        {item.name}
                      </Typography>
                    ))
                  ) : (
                    <Typography sx={{ color: 'text.secondary', fontSize: 12, paddingLeft: 2 }}>
                      No Kalyan Kalpataru content found.
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>

              {/* Vivek Vani Accordion */}
              <Accordion
                sx={{
                  backgroundColor: "#FFEBD5",
                  margin: "-14px",
                  marginTop: "10px",
                  border: "2px solid white",
                  '&.Mui-expanded': {
                    margin: "-14px",
                    marginTop: "10px",
                    minHeight: 'auto',
                  },
                  '& .MuiAccordionSummary-root': {
                    minHeight: '48px',
                  }
                }}
                // expanded={expanded === 'vivekVaniPanel'}
                
                onChange={handleChange('vivekVaniPanel')}
                onClick={() => {navigate('/magazines/vivekvani', { state: { vivekVaniId: Product.VivekVani, magazineLanguage: "bangla" } })}}
              >
                <AccordionSummary expandIcon={null} >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <img src={magazinesIcon} alt="Magazine" style={{ width: 18, height: 18 }} />
                    <Typography component="span" sx={{ fontFamily: 'chanakyaUni', fontSize: 18, fontWeight: 600, color: "#5f452c" }}>{t("vivek_vani_bangla_tr")}</Typography>
                  </Box>
                </AccordionSummary>



                {/* <AccordionDetails style={{ backgroundColor: "white" }}>
                  {vivekVani.length > 0 ? (
                    vivekVani.map((item: VivekVaniItem) => (
                      <Typography onClick={()=>navigate("/magazines/vivekvani",{state:{vivekVaniId:item.id, vivekVaniName:item.name}})} key={item.id} sx={{ color: 'text.secondary', fontSize: 12, borderBottom: "1px solid whitesmoke", paddingTop: 1.2, paddingBottom: 1.2, marginRight: -2, marginLeft: -2, paddingLeft: 2 }}>
                        {item.name}
                      </Typography>
                    ))
                  ) : (
                    <Typography sx={{ color: 'text.secondary', fontSize: 12, paddingLeft: 2 }}>
                      No Vivek Vani content found.
                    </Typography>
                  )}
                </AccordionDetails> */}
              </Accordion>

              {/* Audios Accordion */}
              <Accordion
                sx={{
                  backgroundColor: "#FFEBD5",
                  margin: "-14px",
                  marginTop: "10px",
                  border: "2px solid white",
                  '&.Mui-expanded': {
                    margin: "-14px",
                    marginTop: "10px",
                    minHeight: 'auto',
                  },
                  '& .MuiAccordionSummary-root': {
                    minHeight: '48px',
                  }
                }}
                expanded={expanded === 'audioPanel'}
                onChange={handleChange('audioPanel')}
              >
                <AccordionSummary>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <img src={audioIcon} alt="Audios" style={{ width: 18, height: 18 }} />
                    <Typography component="span" sx={{ fontFamily: 'chanakyaUni', fontSize: 18, fontWeight: 600, color: "#5f452c" }}>{t("Audios_tr")}</Typography>
                  </Box>
                </AccordionSummary>

                <AccordionDetails style={{ backgroundColor: "white" }}>
                  {audios.length > 0 ? (
                    audios.map((item: audiosItem) => (
                      <Typography onClick={() => navigate("/mobileAudioListPage", { state: { categoryId: item.id, type: "audios" } })} key={item.id} sx={{ color: 'text.secondary', fontSize: 12, borderBottom: "1px solid whitesmoke", paddingTop: 1.2, paddingBottom: 1.2, marginRight: -2, marginLeft: -2, paddingLeft: 2 }}>
                        <img src={audioIcon} alt="" style={{ width: 14, height: 14, verticalAlign: 'middle', marginRight: 8 }} />
                        {item.name}
                      </Typography>
                    ))
                  ) : (
                    <Typography sx={{ color: 'text.secondary', fontSize: 12, paddingLeft: 2 }}>
                      No Audios found.
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>

              {/* Pravachan Accordion */}
              <Accordion
                sx={{
                  backgroundColor: "#FFEBD5",
                  margin: "-14px",
                  marginTop: "10px",
                  border: "2px solid white",
                  '&.Mui-expanded': {
                    margin: "-14px",
                    marginTop: "10px",
                    minHeight: 'auto',
                  },
                  '& .MuiAccordionSummary-root': {
                    minHeight: '48px',
                  }
                }}
                expanded={expanded === 'pravachanPanel'}
                onChange={handleChange('pravachanPanel')}
              >
                <AccordionSummary>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <img src={pravachanIcon} alt="Pravachan" style={{ width: 18, height: 18 }} />
                    <Typography component="span" sx={{ fontFamily: 'chanakyaUni', fontSize: 18, fontWeight: 600, color: "#5f452c" }}>{t("Pravachan_tr")}</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails style={{ backgroundColor: "white" }}>
                  {pravachans.length > 0 ? (
                    pravachans.map((item: PravachanItem) => {
                      const id = String(item.id);
                      const isOtherOrSpecial = id.toLowerCase() === "other" || id.toLowerCase() === "special";
                      return (
                      <Typography onClick={() => {
                        if (id.toLowerCase() === "other") {
                          navigate("/mobileAudioListPage", { state: { type: "pravachans", isOther: true, authorName: item.name } });
                        } else if (id.toLowerCase() === "special") {
                          navigate("/mobileAudioListPage", { state: { type: "pravachans", isSpecial: true, authorName: item.name, sortOption: "4" } });
                        } else {
                          navigate("/mobileAudioListPage", { state: { type: "pravachans", authorId: item.id, authorName: item.name } });
                        }
                      }} key={item.id} sx={{ color: 'text.secondary', fontSize: 12, borderBottom: "1px solid whitesmoke", paddingTop: 1.2, paddingBottom: 1.2, marginRight: -2, marginLeft: -2, paddingLeft: 2 }}>
                        {isOtherOrSpecial ? (
                          <img src={pravachanIcon} alt="" style={{ width: 14, height: 14, verticalAlign: 'middle', marginRight: 8 }} />
                        ) : (
                          <PersonIcon sx={{ fontSize: 14, color: "#f97316", verticalAlign: 'middle', mr: 1 }} />
                        )}
                        {item.name}
                      </Typography>
                      );
                    })
                  ) : (
                    <>
                      <Typography sx={{ color: 'text.secondary', fontSize: 12, borderBottom: "1px solid whitesmoke", paddingTop: 1.2, paddingBottom: 1.2, marginRight: -2, marginLeft: -2, paddingLeft: 2 }}>
                        <PersonIcon sx={{ fontSize: 14, color: "#f97316", verticalAlign: 'middle', mr: 1 }} />
                        {t("Honorable_Shri_Jayadayal_Ji")}
                      </Typography>
                      <Typography sx={{ color: 'text.secondary', fontSize: 12, borderBottom: "1px solid whitesmoke", paddingTop: 1.2, paddingBottom: 1.2, marginRight: -2, marginLeft: -2, paddingLeft: 2 }}>
                        <PersonIcon sx={{ fontSize: 14, color: "#f97316", verticalAlign: 'middle', mr: 1 }} />
                        {t("Honorable_Shri_Hanumanprasad_ji_poddar")}
                      </Typography>
                      <Typography sx={{ color: 'text.secondary', fontSize: 12, borderBottom: "1px solid whitesmoke", paddingTop: 1.2, paddingBottom: 1.2, marginRight: -2, marginLeft: -2, paddingLeft: 2 }}>
                        <PersonIcon sx={{ fontSize: 14, color: "#f97316", verticalAlign: 'middle', mr: 1 }} />
                        {t("Honorable_Swami_Shri_Ramsukhdas_Ji")}
                      </Typography>
                    </>
                  )}
                </AccordionDetails>
              </Accordion>

              {/* Video Accordion */}
              <Accordion
                sx={{
                  backgroundColor: "#FFEBD5",
                  margin: "-14px",
                  marginTop: "10px",
                  border: "2px solid white",
                  '&.Mui-expanded': {
                    margin: "-14px",
                    marginTop: "10px",
                    minHeight: 'auto',
                  },
                  '& .MuiAccordionSummary-root': {
                    minHeight: '48px',
                  }
                }}
                expanded={expanded === 'videoPanel'}
                onChange={handleChange('videoPanel')}
              >
                <AccordionSummary>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <VideocamIcon sx={{ fontSize: 18, color: "#f97316" }} />
                    <Typography component="span" sx={{ fontFamily: 'chanakyaUni', fontSize: 18, fontWeight: 600, color: "#5f452c" }}>{t("Video_tr")}</Typography>
                  </Box>
                </AccordionSummary>

                <AccordionDetails style={{ backgroundColor: "white" }}>
                  {videos.length > 0 ? (
                    videos.map((item: VideoItem) => (
                      <Typography onClick={() => {
                        void (async () => {
                          const slug = await resolveCategorySlug(item, (id) => VideoService.GetCategoryDataById(id), "video_category_click");
                          if (!slug) return;
                          navigate(`/videos/category/${slug}`, {
                            state: {
                              categoryId: item.id,
                              categoryName: item.name,
                              categorySlug: slug,
                            },
                          });
                        })();
                      }} key={item.id} sx={{ color: 'text.secondary', fontSize: 12, borderBottom: "1px solid whitesmoke", paddingTop: 1.2, paddingBottom: 1.2, marginRight: -2, marginLeft: -2, paddingLeft: 2 }}>
                        <VideocamIcon sx={{ fontSize: 14, color: "#f97316", verticalAlign: 'middle', mr: 1 }} />
                        {item.name}
                      </Typography>
                    ))
                  ) : (
                    <Typography sx={{ color: 'text.secondary', fontSize: 12, paddingLeft: 2 }}>
                      No Videos found.
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>

              {/* Articles Accordion (Add if you want to display articles) */}
              {/* This section was not in your original "loaded" state, but included for completeness if you decide to add it */}
              <Accordion
                sx={{
                  backgroundColor: "#FFEBD5",
                  margin: "-14px",
                  marginTop: "10px",
                  border: "2px solid white",
                  '&.Mui-expanded': {
                    margin: "-14px",
                    marginTop: "10px",
                    minHeight: 'auto',
                  },
                  '& .MuiAccordionSummary-root': {
                    minHeight: '48px',
                  }
                }}
                expanded={expanded === 'articlesPanel'}
                onChange={handleChange('articlesPanel')}
              >
                <AccordionSummary>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ArticleIcon sx={{ fontSize: 18, color: "#f97316" }} />
                    <Typography component="span" sx={{ fontFamily: 'chanakyaUni', fontSize: 18, fontWeight: 600, color: "#5f452c" }}>{t("Article_tr")}</Typography>
                  </Box>
                </AccordionSummary>

                <AccordionDetails style={{ backgroundColor: "white" }}>
                  {articles.length > 0 ? (
                    articles.map((item: ArticleItem) => (
                      <Typography onClick={() => {
                        void (async () => {
                          const slug = await resolveCategorySlug(item, (id) => ArticlesService.GetCategoryDataById(id), "article_category_click");
                          if (!slug) return;
                          navigate(`/articles/category/${slug}`, {
                            state: {
                              articleCategoryId: item.id,
                              articleCategoryName: item.name,
                              catId: item.id,
                              catName: item.name,
                              catSlug: slug,
                            },
                          });
                        })();
                      }} key={item.id} sx={{ color: 'text.secondary', fontSize: 12, borderBottom: "1px solid whitesmoke", paddingTop: 1.2, paddingBottom: 1.2, marginRight: -2, marginLeft: -2, paddingLeft: 2 }}>
                        <ArticleIcon sx={{ fontSize: 14, color: "#f97316", verticalAlign: 'middle', mr: 1 }} />
                        {item.name}
                        
                      </Typography>
                    ))
                  ) : (
                    <Typography sx={{ color: 'text.secondary', fontSize: 12, paddingLeft: 2 }}>
                      No Articles found.
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>
            </>
          )}
        </>
      )}
    </Box>
  );
};
// --- MobileHomePage Component ---

<MobileHome />